import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Building2 } from 'lucide-react';
import { publicPrelevementApi } from '../../lib/api';

// Page publique de signature d'un mandat de prélèvement SEPA (lot prélèvements, 2026-09-26). Stripe.js
// est chargé directement depuis stripe.com (jamais empaqueté avec le reste de l'app) : c'est une exigence
// de Stripe pour rester hors du périmètre PCI le plus large — le formulaire IBAN tourne dans un iframe
// isolé que ni ce composant ni le serveur ne peuvent jamais lire.
declare global {
    interface Window { Stripe?: (key: string) => any }
}

const loadStripeJs = (() => {
    let promise: Promise<void> | null = null;
    return () => {
        if (!promise) {
            promise = new Promise((resolve, reject) => {
                if (window.Stripe) { resolve(); return; }
                const script = document.createElement('script');
                script.src = 'https://js.stripe.com/v3/';
                script.onload = () => resolve();
                script.onerror = () => reject(new Error("Stripe n'a pas pu se charger."));
                document.head.appendChild(script);
            });
        }
        return promise;
    };
})();

interface Info {
    status: 'en_attente' | 'actif' | 'echoue' | 'revoque';
    company_name?: string;
    contact_label?: string;
    contact_email?: string;
    publishable_key?: string;
    client_secret?: string;
}

export const MandatPrelevementPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = React.useState<Info | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [envoi, setEnvoi] = React.useState(false);
    const [mandateHtml, setMandateHtml] = React.useState('');
    const [ibanComplete, setIbanComplete] = React.useState(false);
    const ibanContainerRef = React.useRef<HTMLDivElement>(null);
    const stripeRef = React.useRef<any>(null);
    const elementsRef = React.useRef<any>(null);
    const ibanElementRef = React.useRef<any>(null);

    React.useEffect(() => {
        if (!token) return;
        publicPrelevementApi.getInfo(token)
            .then((d: any) => {
                if (d?.error) { setErreur(d.error); return; }
                setInfo(d);
            })
            .catch((e: any) => setErreur(e?.message || 'Lien invalide ou expiré.'))
            .finally(() => setLoading(false));
    }, [token]);

    React.useEffect(() => {
        if (!info || info.status !== 'en_attente' || !info.publishable_key || !info.client_secret) return;
        let annule = false;
        loadStripeJs().then(() => {
            if (annule || !window.Stripe || !ibanContainerRef.current) return;
            const stripe = window.Stripe(info.publishable_key!);
            const elements = stripe.elements();
            const iban = elements.create('iban', { supportedCountries: ['SEPA'], placeholderCountry: 'FR' });
            iban.mount(ibanContainerRef.current);
            iban.on('change', (event: any) => {
                setIbanComplete(!!event.complete && !event.error);
                if (event.mandate) setMandateHtml(event.mandate);
            });
            stripeRef.current = stripe;
            elementsRef.current = elements;
            ibanElementRef.current = iban;
        }).catch((e) => setErreur(e?.message || "Stripe n'a pas pu se charger."));
        return () => {
            annule = true;
            try { ibanElementRef.current?.unmount(); } catch {}
        };
    }, [info]);

    const confirmer = async () => {
        if (!token || !stripeRef.current || !ibanElementRef.current || !info?.client_secret) return;
        setEnvoi(true);
        setErreur('');
        try {
            const { setupIntent, error } = await stripeRef.current.confirmSepaDebitSetup(info.client_secret, {
                payment_method: {
                    sepa_debit: ibanElementRef.current,
                    billing_details: { name: info.contact_label || '', email: info.contact_email || '' },
                },
            });
            if (error) { setErreur(error.message || "L'autorisation n'a pas pu être confirmée."); setEnvoi(false); return; }
            if (setupIntent?.status !== 'succeeded' && setupIntent?.status !== 'processing') {
                setErreur("L'autorisation n'a pas pu être confirmée. Vérifiez votre IBAN et réessayez.");
                setEnvoi(false);
                return;
            }
            const res: any = await publicPrelevementApi.confirmer(token);
            if (res?.error) { setErreur(res.error); setEnvoi(false); return; }
            setInfo((prev) => (prev ? { ...prev, status: 'actif' } : prev));
        } catch (e: any) {
            setErreur(e?.message || 'Erreur lors de la confirmation.');
        } finally {
            setEnvoi(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }
    if (!info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4 text-center">
                <Building2 size={32} className="mx-auto mb-3 text-content-muted opacity-40" />
                <p className="text-content font-bold mb-1">Lien invalide</p>
                <p className="text-content-muted text-sm">{erreur || "Ce lien n'est plus valide."}</p>
            </div>
        );
    }
    if (info.status === 'actif') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4 text-center">
                <CheckCircle2 size={36} className="mx-auto mb-3 text-emerald-500" />
                <p className="text-content font-bold mb-1">Autorisation enregistrée</p>
                <p className="text-content-muted text-sm max-w-sm">
                    Votre autorisation de prélèvement est active. Vous serez prévenu(e) par email avant chaque prélèvement.
                </p>
            </div>
        );
    }
    if (info.status === 'revoque' || info.status === 'echoue') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4 text-center">
                <p className="text-content font-bold mb-1">Ce lien n'est plus actif</p>
                <p className="text-content-muted text-sm">Contactez directement l'établissement si vous pensez qu'il s'agit d'une erreur.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-surface-app flex flex-col items-center py-10 px-4">
            <div className="w-full max-w-md bg-surface border border-border rounded-2xl p-6 space-y-5">
                <div className="text-center space-y-1">
                    <p className="text-xs font-bold text-content-muted uppercase tracking-widest">{info.company_name}</p>
                    <h1 className="text-lg font-bold text-content">Autorisation de prélèvement SEPA</h1>
                    <p className="text-xs text-content-muted">
                        Renseignez votre IBAN ci-dessous — il est transmis directement à Stripe, {info.company_name} n'y a jamais accès.
                    </p>
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">IBAN</label>
                    <div ref={ibanContainerRef} className="bg-surface-soft border border-border rounded-xl px-3 py-3" />
                </div>

                {mandateHtml && (
                    <div className="text-[11px] text-content-muted leading-relaxed p-3 rounded-xl bg-surface-soft border border-border" dangerouslySetInnerHTML={{ __html: mandateHtml }} />
                )}

                {erreur && <p className="text-xs text-red-400 font-semibold">{erreur}</p>}

                <button type="button" onClick={confirmer} disabled={!ibanComplete || envoi}
                    className="w-full px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                    {envoi ? <><Loader2 size={15} className="animate-spin" /> Confirmation...</> : 'Autoriser le prélèvement'}
                </button>
                <p className="text-[11px] text-content-muted text-center">Vous pourrez révoquer cette autorisation à tout moment auprès de {info.company_name}.</p>
            </div>
        </div>
    );
};
